/**
 * Typed client for the Cloudflare Worker backend (worker/src/index.ts). Only the
 * endpoints the app calls directly are wired here; the fetch implementation is
 * injectable for testing, mirroring RemoteContentSource's FetchLike pattern.
 */
import { Entitlements } from "../billing/entitlements";
import { ModerationStatus, UserModeration } from "../billing/moderation";
import { CatalogsResponse, parseCatalogsResponse } from "../providers/types";
import { ProviderMetadata, parseProviderMetadata } from "../providers/providerMetadata";
import { ProgressSubmission } from "../results/progressSubmission";
import { SyncedMediaProgress } from "../modules/media/mediaProgress";
import { UsageSubmission, UsageSubmissionResponse } from "../usage/usageSubmission";
import { UserStateDomain, UserStateEnvelope } from "../userstate/envelope";

// Re-exported so callers only need this one import path for both the request + response
// shape, mirroring `ProgressSubmissionResponse`'s own inline declaration below.
export type { UsageSubmission, UsageSubmissionResponse };

// Re-exported so a caller (`src/ui/media/useMediaProgressSync.ts`) doesn't need a
// second import path just to type a `putMediaProgress`/`getMediaProgress` payload.
export type { SyncedMediaProgress };

// Re-exported so callers (apps/admin, session.tsx) don't need a second import from
// `billing/moderation` just to type a `me()`/admin-moderation result.
export type { ModerationStatus, UserModeration };

export interface HttpResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export type HttpFetch = (
  url: string,
  init: { method: string; headers: Record<string, string>; body?: string; binaryBody?: Blob },
) => Promise<HttpResponse>;

/** Server response from /redeem and /token-from-subscription. */
export interface TokenGrant {
  token: string;
  expiresAt: number;
  entitlements: Entitlements;
}

/** Server response from /auth/apple — the public user + the worker session id. */
export interface AppleSignInResult {
  user: { id: string; email: string; role: string; firstName?: string; lastName?: string };
  session: string;
}

// ── Admin types (mirror worker/src/types.ts) ────────────────────────────────
export interface AdminUser {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  /**
   * Epoch seconds an admin soft-deleted this account (docs/013-ADMIN-APP-PLAN.md
   * People-tab overhaul); absent/`undefined` = active. Distinct from
   * `UserModeration` ban/suspend, which stays visible — a soft-deleted user is
   * hidden from the default `adminUsers()` list (see its `includeDeleted` option)
   * and restorable via `adminRestoreUser`.
   */
  deletedAt?: number;
  /**
   * Epoch seconds this account was **pre-provisioned by an admin** via
   * {@link BackendClient.adminCreateUser} (docs/286-ADMIN-ADD-USER-WITHOUT-
   * INVITE.md v1), before the person has ever signed in themselves; absent for an
   * account created the normal way. A provisioned-but-unclaimed account
   * (`provisionedAt` set, `claimedAt` unset/`null`) is credential-less — it's
   * adopted by that person's first VERIFIED-identity sign-in (Sign in with Apple
   * today), not a login.
   */
  provisionedAt?: number;
  /** Epoch seconds the account above was first claimed by a verified identity;
   * `null`/absent while still unclaimed. Only meaningful alongside `provisionedAt`. */
  claimedAt?: number | null;
}
export interface GrantSpec {
  kind: "pack" | "catalog";
  ref: string;
  /**
   * Time-boxed access (docs/306-CATALOG-ACCESS-PROTECTION-AND-TIMED-GRANTS.md v0) —
   * epoch seconds this grant stops being active; `null`/absent = never expires.
   */
  expiresAt?: number | null;
}
export interface AdminInvite {
  code: string;
  active: boolean;
  expiresAt: number | null;
  maxUses: number | null;
  usedCount: number;
  createdAt: number;
  grants?: GrantSpec[];
  /** Admin-only internal note — never shown to the end user (docs/290). */
  note?: string | null;
  /** User-facing welcome line shown once the invite validates on the OIDC
   * `/authorize` interstitial (docs/290-INVITE-DESCRIPTION-FIELDS.md). */
  message?: string | null;
}
/**
 * One recipient of an invite email (docs/273-ADMIN-INVITE-EMAILS.md v0). One invite
 * `code` (`maxUses > 1`) can be emailed to many recipients, each tracked
 * independently.
 */
export interface AdminInviteDelivery {
  id: string;
  code: string;
  email: string;
  status: "pending" | "sent" | "failed" | "bounced";
  providerId: string | null;
  error: string | null;
  createdAt: number;
  sentAt: number | null;
}

/**
 * One historical attempt to use an invite code — a full audit trail, distinct from
 * `AdminInvite.usedCount` (successes only, no email/timestamp/failure detail).
 * `email` is null when the flow didn't collect one (the OIDC invite-login page, or a
 * hidden Apple private-relay address). `reason` is set only on `outcome: "invalid"`.
 */
export interface AdminInviteAttempt {
  id: string;
  email: string | null;
  code: string;
  outcome: "accepted" | "invalid";
  reason: string | null;
  at: number;
}
export interface AdminGrant extends GrantSpec {
  id: string;
  userId: string;
  grantedAt: number;
  grantedBy: string | null;
}
/**
 * One entry per (deduplicated, normalized) input email — the data-minimal
 * response of {@link BackendClient.adminUsersLookup}
 * (docs/359-BULK-EMAIL-LOOKUP-AND-ACCESS-GIFTING.md v0): enough to triage +
 * gift against (`userId`, coarse account state), never the full profile —
 * that stays {@link BackendClient.adminUserHistory}'s deliberate one-user
 * drill-down.
 */
export interface AdminUserLookupResult {
  email: string;
  exists: boolean;
  userId?: string;
  claimedAt?: number | null;
  provisionedAt?: number | null;
  grantCount?: number;
}
/** One matched account's outcome from {@link BackendClient.adminGrantBulk} —
 * never a bare success/failure count (docs/359 v0 §3.3). */
export interface AdminBulkGrantResult {
  userId: string;
  outcome: "granted" | "already-had" | "skipped";
  /** Set only when `outcome === "skipped"` (e.g. "user not found"). */
  reason?: string;
  granted: GrantSpec[];
  alreadyHad: GrantSpec[];
}
export interface AdminFeedback {
  id: string;
  userId: string | null;
  email: string | null;
  message: string;
  createdAt: number;
}

// ── Content moderation ("Report") types (docs/072-DISTRIBUTION-AND-STORE-COMPLIANCE.md §B2) ─
export type ReportTargetType = "catalog" | "pack" | "lesson";
export const REPORT_REASONS = ["objectionable", "copyright", "spam", "other"] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export interface ReportBody {
  targetType: ReportTargetType;
  /** The reported entity's canonical id (pack/catalog id, or `packId#lessonId` for a lesson). */
  targetId: string;
  /** The manifest/catalog URL the reporter was viewing, for triage. */
  url?: string;
  reason: ReportReason;
  note?: string;
  /**
   * Optional follow-up address the reporter typed (docs/282-REPORT-ANONYMITY-AND-
   * MY-REPORTS.md v0). The report stays anonymous unless this is set — it is NEVER
   * derived from a signed-in session automatically.
   */
  email?: string;
}

// ── Provider progress submission (docs/304-PROVIDER-PROGRESS-SUBMISSION.md v0) ──
/** `POST /progress`'s response — how many of the submitted items were accepted
 * (a malformed individual item is skipped, never fails the whole batch). */
export interface ProgressSubmissionResponse {
  ok: boolean;
  accepted: number;
}

// ── Cross-device media/narration resume sync (docs/304-PROVIDER-PROGRESS-
// SUBMISSION.md Part B v0) ──
/** `POST /media-progress`'s response — mirrors `ProgressSubmissionResponse`'s own
 * shape; `accepted` counts well-formed items ATTEMPTED, not necessarily each a
 * winning LWW write. */
export interface MediaProgressPutResponse {
  ok: boolean;
  accepted: number;
}
/** `GET /media-progress`'s response — the caller's own positions, newest-`updatedAt`-
 * first. */
export interface MediaProgressListResponse {
  items: SyncedMediaProgress[];
}

// ── Unified user-state envelope + sync (docs/361-UNIFIED-USER-STATE-SCHEMA-AND-
// SYNC.md v1) — the general `POST`/`GET /user-state` pair, generalizing docs/304
// Part B's own `/media-progress` from one domain to any `UserStateDomain`. ──
/** `POST /user-state`'s response — `accepted` mirrors every other batch-submit
 * response's own contract (well-formed items ATTEMPTED, not necessarily each a
 * winning LWW write); `records` is the POST-MERGE winner for each item that was
 * attempted (in submission order) — so a caller doesn't need a second `GET` just to
 * see what it now holds. */
export interface UserStatePostResponse {
  ok: boolean;
  accepted: number;
  records: UserStateEnvelope[];
}
/** `GET /user-state`'s response — the caller's own envelopes across every one of
 * their devices, newest-`updatedAt`-first. */
export interface UserStateGetResponse {
  records: UserStateEnvelope[];
}

/** One stored progress row, as `GET /admin/progress` returns it (the admin
 * Progress tab's list) — mirrors `worker/src/types.ts`'s `ProgressRow`, with
 * `detail` already JSON-decoded back into an object server-side. */
export interface AdminProgressRow {
  id: string;
  userId: string;
  profileId: string;
  kind: "lesson" | "pack" | "catalog" | "bible" | "quiz" | "assessment";
  refId: string;
  packId: string | null;
  packTitle: string | null;
  title: string;
  detail: Record<string, unknown>;
  /** Epoch **milliseconds** (the app's own clock — the one exception to this
   * row's otherwise epoch-seconds fields) — format as `new Date(lastAt)`. */
  lastAt: number;
  /** Epoch seconds (the worker's own clock) — format as `new Date(updatedAt * 1000)`. */
  updatedAt: number;
}

export const REPORT_STATUSES = ["open", "reviewing", "actioned", "dismissed"] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export interface AdminReport {
  id: string;
  targetType: ReportTargetType;
  targetId: string;
  url: string | null;
  reason: ReportReason;
  note: string | null;
  userId: string | null;
  createdAt: number;
  status: ReportStatus;
  resolution?: string | null;
  handledBy?: string | null;
  handledAt?: number | null;
  notes?: string | null;
  /** Reporter-typed follow-up address (docs/282 v0), or `null` if not provided. */
  email: string | null;
}

// ── Admin manifest builder (docs/168-ADMIN-MANIFEST-BUILDER.md v0) ─
/** One file in the gated-bucket listing the manifest builder browses. */
export interface AdminContentFile {
  key: string;
  size: number;
  uploaded?: string;
}
/** `GET /admin/content/list` — one level (delimiter "/") of the gated content bucket. */
export interface AdminContentListing {
  bucket?: "public" | "gated";
  prefix: string;
  folders: string[];
  files: AdminContentFile[];
}
export type AdminFileBucket = "public" | "gated";
export interface AdminMultipartUpload {
  uploadId: string;
  key: string;
  bucket: AdminFileBucket;
  partSize: number;
  partCount: number;
  expiresAt: number;
}
export interface AdminStoredFile {
  key: string;
  bucket: AdminFileBucket;
  size: number;
  etag?: string | null;
}
/** The manifest index row `GET`/`POST /admin/manifests` return (no body). */
export interface AdminManifest {
  id: string;
  title: string;
  updatedAt: number;
}
/**
 * The FULL manifest body `GET /admin/manifests/{id}` returns
 * (docs/343-ADMIN-MANIFEST-BUILDER-REDESIGN.md v0) — `manifest` is the raw, unparsed
 * JSON object exactly as last saved; the caller runs its own `parseManifest` on it.
 */
export interface AdminManifestDetail {
  id: string;
  title: string;
  manifest: unknown;
  updatedAt: number;
}
/** The catalog-document index row `GET`/`POST /admin/catalog-docs` return (no body). */
export interface AdminCatalogDoc {
  id: string;
  title: string;
  updatedAt: number;
}
/** The FULL catalog-document body `GET /admin/catalog-docs/{id}` returns — the
 *  catalog-document sibling of {@link AdminManifestDetail}. */
export interface AdminCatalogDocDetail {
  id: string;
  title: string;
  catalog: unknown;
  updatedAt: number;
}

// ── Provider device/usage analytics + provider config (docs/300-PROVIDER-
// DEVICE-ANALYTICS-AND-DISCLOSURE.md v0, appVersion/byVersion/GET /admin/devices v1)
// ─────────────────────────────────────────────────────────────────────────
/** `GET /admin/devices/stats`'s aggregate. */
export interface AdminDeviceStats {
  total: number;
  byType: Record<string, number>;
  /** Count per most-recently-seen app version (docs/300 v1) — a device that has
   * never sent `X-Client-Version` is bucketed under the literal key `"unknown"`. */
  byVersion: Record<string, number>;
  activeLastNDays: number;
  activeDays: number;
  perAccount: { userId: string; count: number }[];
}
/**
 * One-round-trip landing-dashboard read model (`GET /admin/overview`,
 * docs/340-ADMIN-MANTIS-AND-API-IDEAS.md v1 §4.1) — pre-aggregated so the
 * Mantis Overview page (the new landing route, replacing the bare `/people`
 * redirect) doesn't N+1 across a dozen endpoints just to answer "is my
 * provider healthy?" at a glance. Every count is a cheap server-side
 * aggregate over data this admin already has other pages for — this is a
 * summary, not a new data source.
 */
export interface AdminOverview {
  users: { total: number; admins: number };
  /** Reuses the SAME "active in the last N days" window `GET /admin/devices/stats`
   * defaults to (`Env.DEVICE_ANALYTICS_ACTIVE_DAYS`, default 30) — the two
   * numbers agree by construction. */
  devices: { total: number; activeLastNDays: number; activeDays: number };
  /** `"open"` + `"reviewing"` — not yet dismissed/actioned (docs/105/282). */
  reports: { pending: number; total: number };
  invites: { active: number; total: number };
  /** Active (non-deleted) accounts created within the last `days`. */
  recentSignups: { days: number; count: number };
}

/** One device a single account has connected with (`GET /admin/users/devices`,
 * docs/300 §6) — the per-user detail behind {@link AdminDeviceStats}'s aggregate
 * `perAccount` count; also the shape `GET /admin/devices` (v1, every account at
 * once) and the self-scoped `GET /devices` return. Data-minimal by
 * construction: a coarse `deviceType` + the raw `platform` hint + first/
 * last-seen, never a fingerprint/IP/location. `appVersion` (v1) is the
 * device's most-recently-seen app version, or `null` when it never sent
 * `X-Client-Version` (every pre-v1 recording, and any client that structurally
 * can't send one, e.g. a plain browser). `userId` was always present on the
 * wire (the Worker's `DeviceRow` carries it) but undeclared here until v1's
 * `GET /admin/devices` needed it to attribute a row to an account across
 * every device at once — `GET /devices`/`GET /admin/users/devices` scope to
 * one account already, so it's redundant (but harmless) there. */
export interface AdminDevice {
  id: string;
  userId: string;
  deviceId: string;
  deviceType: string;
  platform: string | null;
  appVersion: string | null;
  firstSeenAt: number;
  lastSeenAt: number;
}
/** The DB-backed `"default"` provider-config singleton row (docs/300 v0 §3.3,
 * extended by docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md v0 §3.2) — `null`
 * (via `AdminProviderConfigResult.config`) when never saved. */
export interface AdminProviderConfig {
  id: "default";
  name: string | null;
  logoUrl: string | null;
  termsUrl: string | null;
  trackingDisclosureText: string | null;
  trackingDisclosureUrl: string | null;
  trackingDisclosureVersion: number;
  /** docs/312 v0 §3.1 — the provider's own acceptance-gated terms of usage
   * (distinct from the plain, unversioned `termsUrl` above). `providerTermsVersion`
   * `0` means "not configured" — a DIFFERENT convention than
   * `trackingDisclosureVersion`'s own "always ≥1." */
  providerTermsTitle: string | null;
  providerTermsText: string | null;
  providerTermsUrl: string | null;
  providerTermsRequired: boolean | null;
  providerTermsVersion: number;
  /** docs/312 v0 §3.1/§8 Q5 — provider-level legal links, no acceptance/versioning. */
  privacyUrl: string | null;
  supportUrl: string | null;
  contentPolicyUrl: string | null;
  /** docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0 §5 — per-provider
   * opt-out toggles for the admin page-access beacon (`POST /admin/track`)
   * and its `request.cf.country` geo attach. `null` = ON (the default); an
   * explicit `false` opts THIS deployment out. */
  adminAccessTrackingEnabled: boolean | null;
  adminGeoEnabled: boolean | null;
  updatedAt: number | null;
  updatedBy: string | null;
}
/** `GET /admin/provider-config`'s response — the stored row (or `null`) plus the
 * env-var fallback each field would resolve to if left unset. */
export interface AdminProviderConfigResult {
  config: AdminProviderConfig | null;
  defaults: { name: string | null; logoUrl: string | null; termsUrl: string | null };
}
// ── Provider usage analytics (docs/310-PROVIDER-USAGE-ANALYTICS.md v0) ─────
/** `GET /admin/usage/stats`'s aggregate — per-metric totals across every stored
 * counter (a per-catalog/per-pack breakdown is v1) + a QUERY of the existing
 * `tracking_disclosure_acceptances` table (docs/300) grouped by version, never a
 * new capture ("310 surfaces it, 300 captures it"). */
export interface AdminUsageStats {
  totals: { metric: string; count: number }[];
  disclosureAcceptance: { version: number; count: number }[];
}

// ── Admin alerts + monitoring (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0) ──
export type AdminAlertMetric =
  | "cf.quotaPercent"
  | "cf.requestCount"
  | "cf.d1RowsRead"
  | "cf.d1RowsWritten"
  | "cf.r2StorageBytes"
  | "app.errorRatePercent"
  | "app.activeUsers"
  | "app.newUsers"
  | "app.usageCounter"
  | "system.uptimeDown";
export const ADMIN_ALERT_METRICS: readonly AdminAlertMetric[] = [
  "cf.quotaPercent",
  "cf.requestCount",
  "cf.d1RowsRead",
  "cf.d1RowsWritten",
  "cf.r2StorageBytes",
  "app.errorRatePercent",
  "app.activeUsers",
  "app.newUsers",
  "app.usageCounter",
  "system.uptimeDown",
];
export type AdminAlertComparator = "gt" | "gte" | "lt" | "lte";
export const ADMIN_ALERT_COMPARATORS: readonly AdminAlertComparator[] = ["gt", "gte", "lt", "lte"];
export type AdminAlertChannel = "email" | "inApp" | "statusOnly";
export const ADMIN_ALERT_CHANNELS: readonly AdminAlertChannel[] = ["email", "inApp", "statusOnly"];
export type AdminAlertSeverity = "info" | "warning" | "critical";
export const ADMIN_ALERT_SEVERITIES: readonly AdminAlertSeverity[] = ["info", "warning", "critical"];

/** An owner-editable threshold rule (docs/347 v0 §3.1/§6). */
export interface AdminAlertRule {
  id: string;
  label: string;
  metric: AdminAlertMetric;
  metricKey: string | null;
  comparator: AdminAlertComparator;
  threshold: number;
  windowMinutes: number;
  channels: AdminAlertChannel[];
  cooldownMinutes: number;
  consecutiveBreaches: number;
  severity: AdminAlertSeverity;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}
/** The `POST /admin/alert-rules` request body — omit `id` to create, supply it to
 * update (upsert). */
export type AdminAlertRuleInput = Omit<AdminAlertRule, "id" | "createdAt" | "updatedAt"> & { id?: string };

/** One fired/recovered alert episode (docs/347 v0 §3.3) — `GET /admin/alert-events`'s
 * "recent alert history" feed. */
export interface AdminAlertEvent {
  id: string;
  ruleId: string;
  ruleLabel: string;
  metric: AdminAlertMetric;
  metricKey: string | null;
  severity: AdminAlertSeverity;
  status: "open" | "recovered";
  openedAt: number;
  recoveredAt: number | null;
  peakValue: number;
  message: string;
}

/** `GET /admin/monitoring`'s one read model (docs/347 v0 §7). */
export interface AdminMonitoring {
  metrics: {
    ruleId: string;
    label: string;
    metric: AdminAlertMetric;
    metricKey: string | null;
    value: number;
    threshold: number;
    comparator: AdminAlertComparator;
    severity: AdminAlertSeverity;
    breaching: boolean;
    firing: boolean;
  }[];
  unavailableRuleIds: string[];
  heartbeat: { at: number | null; stale: boolean };
  cronCadenceSeconds: number;
}

/** `GET /admin/cost`'s response (docs/347 v0 §7.4) — `available:false` when Cloudflare
 * Analytics credentials aren't configured, or the GraphQL call fails. */
export type AdminCost =
  | {
      available: true;
      since: string;
      until: string;
      report: {
        lines: { metric: string; totalUnits: number; includedUnits: number; billableUnits: number; usdCost: number }[];
        totalUsd: number;
      };
    }
  | { available: false; reason: string };
/** `PUT /admin/provider-config`'s request body — a full replace; an
 * omitted/blank field CLEARS that override back to the env default. */
export interface AdminProviderConfigPatch {
  name?: string | null;
  logoUrl?: string | null;
  termsUrl?: string | null;
  trackingDisclosureText?: string | null;
  trackingDisclosureUrl?: string | null;
  trackingDisclosureVersion?: number;
  providerTermsTitle?: string | null;
  providerTermsText?: string | null;
  providerTermsUrl?: string | null;
  providerTermsRequired?: boolean | null;
  providerTermsVersion?: number;
  /** "Save & require re-acceptance" (docs/312 v0 §3.1/§8 Q3) — never a free-hand
   * integer; see `AdminProviderConfig.providerTermsVersion`'s own doc + the
   * Worker's `resolveProviderTermsVersion` for the full precedence rules. */
  bumpTermsVersion?: boolean;
  privacyUrl?: string | null;
  supportUrl?: string | null;
  contentPolicyUrl?: string | null;
  /** docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0 §5 — omitted/`null` =
   * ON (the default), matching every other feature-toggle field on this
   * patch; full-replace like every other field here (an omitted field
   * clears back to `null`, never "preserve the stored value"). */
  adminAccessTrackingEnabled?: boolean | null;
  adminGeoEnabled?: boolean | null;
}

// ── Admin page-access tracking (docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0) ─
/** One page-access beacon row (`GET /admin/access/stats`' `recent` slice). */
export interface AdminAccessLogRow {
  id: string;
  actorId: string;
  event: "page_view" | "action_view";
  path: string;
  country: string | null;
  at: number;
}
/** `GET /admin/access/stats`' aggregate — a `?days=`-windowed summary over the
 * admin page-access beacon table. `byCountry` is already small-bucket-
 * suppressed (small buckets folded into one `country: null` "other" row). */
export interface AdminAccessStats {
  totalViews: number;
  byPath: { path: string; count: number }[];
  byAdmin: { actorId: string; count: number }[];
  byCountry: { country: string | null; count: number }[];
  activeAdmins: number;
  recent: AdminAccessLogRow[];
}

// ── Provider legal & terms editing (docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md v0) ─
/** One recorded acceptance of a provider/catalog/pack terms declaration
 * (`GET /admin/terms-acceptances`, docs/312 v0 §5.3 — "who accepted which
 * version"). Append-only per `(userId, scope, version)`. */
export interface AdminTermsAcceptance {
  id: string;
  userId: string;
  scope: string;
  version: number;
  acceptedAt: number;
}

// ── Admin per-user history (docs/311-ADMIN-USER-HISTORY.md v0) ─────────────
/** One entry in `GET /admin/users/history`'s merged, newest-first feed — a
 * discriminated union on `type` mirroring the Worker's own `UserHistoryEvent`
 * (`worker/src/userHistory.ts`); `at` is epoch seconds. `report`/`notice`'s
 * `reason`/`kind`/`status` are kept as plain `string` here (not the Worker's own
 * narrower enums) — this client has no reason to duplicate those unions just to
 * render a label. */
export type AdminHistoryEvent =
  | { type: "account"; at: number; event: "created" | "provisioned" | "claimed" }
  | { type: "invite"; at: number; code: string; outcome: "accepted" | "invalid"; reason: string | null }
  | {
      type: "report";
      at: number;
      reportId: string;
      targetType: string;
      targetId: string;
      reason: string;
      status: string;
    }
  | { type: "feedback"; at: number; feedbackId: string; excerpt: string }
  | {
      type: "grant";
      at: number;
      kind: "pack" | "catalog";
      ref: string;
      expiresAt: number | null;
      grantedBy: string | null;
      action: "applied";
    }
  | { type: "notice"; at: number; noticeId: string; kind: string; excerpt: string; readAt: number | null }
  | { type: "admin"; at: number; action: string; actorId: string; targetRef: string | null; reason: string | null };

// ── Moderation actions (docs/105-ADMIN-MODERATION-AND-MEMBERSHIP-ENFORCEMENT.md v0) ─
export type ReportDecision = "dismiss" | "takedown";

export interface AuditEvent {
  id: string;
  actorId: string;
  action: string;
  targetUserId?: string | null;
  targetRef?: string | null;
  reason?: string | null;
  at: number;
  /** `request.cf.country` at write time (docs/357-ADMIN-ANALYTICS-AND-ACCESS-
   * TRACKING.md v0 §3.3) — populated only at the write sites that had a
   * `Request` in scope (see the Worker's own `AuditEvent.country` doc);
   * `null`/absent on every other action, incl. every pre-357 event. */
  country?: string | null;
}

export interface AdminTakedown {
  ref: string;
  reason: string | null;
  by: string;
  at: number;
}

// ── Notices inbox + account/membership moderation (docs/105 v1) ────────────
// "device" (docs/326-MY-CONNECTED-DEVICES.md v0) is server-EMITTED only — never
// hand-sendable by an admin (`adminCreateNotice`/`adminSendNotice`'s `kind` param
// is typed `NoticeKind` too, so this widening technically lets an admin caller pass
// it; the Worker's own `POST /admin/notices` doesn't special-case it either, so
// this mirrors the server's existing (not further restricted) behavior rather than
// splitting a wire-vs-sendable union — see docs/340-ADMIN-MANTIS-AND-API-IDEAS.md
// §3/§9 Q2 for the considered-but-deferred split). "alert" (docs/347-ADMIN-ALERTS-AND-
// MONITORING.md v0) is likewise server-emitted only — the in-app delivery channel for a
// fired alert-rule episode.
export const NOTICE_KINDS = ["report", "takedown", "warning", "suspension", "info", "device", "alert"] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];

export interface Notice {
  id: string;
  userId: string;
  kind: NoticeKind;
  body: string;
  relatedRef?: string | null;
  createdAt: number;
  readAt?: number | null;
}

/** `GET /auth/me`'s result — the public user shape plus the account's current
 * moderation row (`null` if it's never been moderated, treated as `"active"`). */
export interface MeResult {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  moderation: UserModeration | null;
}

export class BackendError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

/**
 * Optional per-client device-analytics headers (docs/300-PROVIDER-DEVICE-
 * ANALYTICS-AND-DISCLOSURE.md v0, `appVersion`/`X-Client-Version` v1) — sent on
 * every request `post`/`authed` make so a provider Worker can record device/
 * usage stats. All optional; any absent → its header simply isn't sent (a
 * Worker never fabricates a missing one, so an omission just means "not
 * recorded," not an error). `clientPlatform` is a coarse hint (`"ios"`/
 * `"android"`/`"web"`/etc, `deviceAnalytics.ts`'s `DeviceType` on the Worker
 * side — this client never validates it, the Worker does); `deviceId` is a
 * caller-supplied, OPAQUE, per-install id — never generated here (that's the UI
 * layer's job, e.g. `src/ui/device/clientMeta.ts`), since a core/framework-free
 * client has no storage seam of its own to persist one in; `appVersion` is the
 * caller's own app version string (e.g. `"1.4.2"`, from `expo-constants`'s
 * `Constants.expoConfig?.version` on the UI side) — sent verbatim, never
 * parsed/validated as semver by either side.
 */
export interface BackendClientMeta {
  clientPlatform?: string;
  deviceId?: string;
  appVersion?: string;
}

export class BackendClient {
  constructor(
    private readonly baseUrl: string,
    private readonly fetchFn: HttpFetch = (url, init) =>
      fetch(url, { method: init.method, headers: init.headers, body: init.binaryBody ?? init.body }),
    private readonly clientMeta: BackendClientMeta = {},
  ) {}

  /** `{ "X-Client-Platform"?, "X-Device-Id"?, "X-Client-Version"? }` from
   * `this.clientMeta` — merged into every request `post`/`authed` build, so a
   * provider Worker's device analytics (docs/300) sees them on every sign-in/
   * refresh call this client makes, without each method threading them
   * individually. */
  private deviceHeaders(): Record<string, string> {
    const h: Record<string, string> = {};
    if (this.clientMeta.clientPlatform) h["X-Client-Platform"] = this.clientMeta.clientPlatform;
    if (this.clientMeta.deviceId) h["X-Device-Id"] = this.clientMeta.deviceId;
    if (this.clientMeta.appVersion) h["X-Client-Version"] = this.clientMeta.appVersion;
    return h;
  }

  /** Redeem a coupon code → content token. `sessionId` enables per-user limits. */
  redeemCoupon(code: string, sessionId?: string): Promise<TokenGrant> {
    return this.post<TokenGrant>("/redeem", { code }, sessionId);
  }

  /** Exchange a verified subscription (RevenueCat/Stripe) → content token. */
  tokenFromSubscription(appUserId: string, sessionId?: string): Promise<TokenGrant> {
    return this.post<TokenGrant>("/token-from-subscription", { appUserId }, sessionId);
  }

  /** Mint a content token for a signed-in user (invited members get `member`). */
  tokenFromSession(sessionId: string): Promise<TokenGrant> {
    return this.post<TokenGrant>("/token-from-session", {}, sessionId);
  }

  /**
   * Sign in with a verified Apple identity token → { user, worker session id }
   * (docs/292-PROVIDER-RETURNING-USER-APPLE-IDENTITY.md v0). A **returning** user —
   * matched server-side by Apple `sub` or a verified email — lands on their same
   * account with no invite; a genuinely **new** account requires `opts.inviteCode`
   * (docs/263-PROVIDER-PAGES-AND-INVITE-ACCESS.md decision B) and rejects with a
   * `403 invite <reason>` `BackendError` when it's missing/invalid — the caller
   * (`appState.tsx`'s `signInToProviderApple`) is expected to catch that specific
   * case and retry with a code once the user supplies one.
   *
   * `opts.firstName`/`opts.lastName`: Apple returns the user's name **only on the
   * FIRST authorization ever**, in the client-side authorization RESPONSE (never
   * in the identity token) — so the caller must pass along `credential.fullName`
   * right after a fresh `signInAsync()` call. Applied server-side only when this
   * sign-in creates a brand-new account; ignored (never overwrites) on a
   * returning/linked account.
   *
   * `opts.nonce` is the raw one-time nonce whose SHA-256 digest was sent to Apple.
   * The provider verifies that digest against the identity token and atomically
   * consumes it, preventing replay of the same Apple credential.
   */
  appleSignIn(
    identityToken: string,
    opts: { inviteCode?: string; firstName?: string; lastName?: string; nonce?: string } = {},
  ): Promise<AppleSignInResult> {
    return this.post<AppleSignInResult>("/auth/apple", {
      identityToken,
      inviteCode: opts.inviteCode,
      firstName: opts.firstName,
      lastName: opts.lastName,
      nonce: opts.nonce,
    });
  }

  /**
   * Create a new account on this backend and sign in → { user, session }
   * (docs/094-CONTENT-PROVIDER-ACCOUNTS.md — used both by the app's own backend and
   * any third-party content-provider backend a user signs into, since the contract
   * is the same either way). Rejects (409) if the email is already registered.
   *
   * An optional `invite` code: this repo's own Worker requires one (docs/275-
   * ADMIN-APP-IOS.md — account creation is gated the same way `signInWithApple`
   * already was, docs/263 decision B) and rejects with 403 when it's missing or
   * invalid; a third-party conforming provider may or may not require one.
   *
   * `opts.firstName`/`opts.lastName` are optional and, when this repo's own Worker
   * is the target, stored on the new account (trimmed; blank → unset).
   */
  register(
    email: string,
    password: string,
    invite?: string,
    opts: { firstName?: string; lastName?: string } = {},
  ): Promise<AppleSignInResult> {
    return this.post<AppleSignInResult>("/auth/register", {
      email,
      password,
      invite,
      firstName: opts.firstName,
      lastName: opts.lastName,
    });
  }

  /** Sign in with email/password → { user, session } (docs/094-CONTENT-PROVIDER-ACCOUNTS.md). */
  login(email: string, password: string): Promise<AppleSignInResult> {
    return this.post<AppleSignInResult>("/auth/login", { email, password });
  }

  /**
   * Rotate a still-valid session into a fresh one (docs/215-PROVIDER-LOGIN-SECURITY-
   * OIDC.md v2 §"POST /auth/refresh for the contract kind") — the contract-auth
   * analog of `refreshOidcToken` (`auth/oidcSignIn.ts`). Throws `BackendError(401)` if
   * the session is missing/expired/invalid, same as `me()`.
   */
  refreshSession(sessionId: string): Promise<AppleSignInResult> {
    return this.authed<AppleSignInResult>("POST", "/auth/refresh", sessionId);
  }

  /**
   * Delete the current session server-side (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md
   * §3 — `POST /auth/logout` existed on the Worker with no client caller, so a
   * sign-out only ever cleared local state and left the session valid until it
   * expired on its own). Idempotent — the endpoint's own doc says so, and it never
   * 401s on an already-invalid/expired session, so callers can call this
   * unconditionally before clearing local credentials without a try/catch.
   */
  logout(sessionId: string): Promise<{ ok: boolean }> {
    return this.authed<{ ok: boolean }>("POST", "/auth/logout", sessionId);
  }

  /**
   * The signed-in user's entitled catalogs + one content token covering the whole
   * set (docs/094-CONTENT-PROVIDER-ACCOUNTS.md §2, `worker/src/catalogs.ts`) — the
   * "generalized redeem code": sign in once, get back everything you're granted.
   * Sent as `X-Session` (matches `tokenFromSession`); the Worker also accepts
   * `Authorization: Bearer` for the same header, per the endpoint's spec.
   */
  async listCatalogs(sessionId: string): Promise<CatalogsResponse> {
    const res = await this.fetchFn(`${this.baseUrl}/catalogs`, {
      method: "GET",
      // `GET /catalogs` is the one request every signed-in client makes on every
      // app launch/pull-to-refresh, so it carries the device-analytics headers too
      // (docs/300 v0) — the Worker records/refreshes the device's `lastSeenAt`
      // here, keeping it current beyond the one-time sign-in moment (and
      // registering a contract/Apple-session device on ongoing use at all).
      headers: { "X-Session": sessionId, ...this.deviceHeaders() },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new BackendError(res.status, (data as { error?: string })?.error ?? `HTTP ${res.status}`);
    }
    const parsed = parseCatalogsResponse(data);
    if (!parsed.ok) throw new BackendError(res.status, parsed.errors.join("; "));
    return parsed.value;
  }

  /**
   * `GET /provider` — this backend's own public, unauthenticated metadata
   * (docs/215-PROVIDER-LOGIN-SECURITY-OIDC.md §2.7/§3c): name/logo/terms + `authKind`
   * (+ `oidc` config when `authKind === "oidc"`), shown on `/add-provider` before the
   * user types anything (anti-phishing) and used to pick the contract-vs-OIDC branch.
   * Every content provider — ours and any third party — serves the same shape.
   */
  async providerMetadata(): Promise<ProviderMetadata> {
    const res = await this.fetchFn(`${this.baseUrl}/provider`, { method: "GET", headers: {} });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new BackendError(res.status, (data as { error?: string })?.error ?? `HTTP ${res.status}`);
    }
    const parsed = parseProviderMetadata(data);
    if (!parsed.ok) throw new BackendError(res.status, parsed.errors.join("; "));
    return parsed.value;
  }

  /** Submit a feedback message. Open to anyone; `sessionId` attributes it to a user. */
  submitFeedback(
    message: string,
    opts: { email?: string; sessionId?: string } = {},
  ): Promise<{ ok: boolean }> {
    return this.post<{ ok: boolean }>("/feedback", { message, email: opts.email }, opts.sessionId);
  }

  /**
   * Submit a "Report" on a catalog/pack/lesson (docs/072-DISTRIBUTION-AND-STORE-COMPLIANCE.md
   * §B2). Open to anyone; `sessionId` attributes it to a signed-in user.
   */
  submitReport(body: ReportBody, sessionId?: string): Promise<{ ok: boolean }> {
    return this.post<{ ok: boolean }>("/report", body, sessionId);
  }

  /**
   * Submit an opt-in learning-progress report to a provider (docs/304-PROVIDER-
   * PROGRESS-SUBMISSION.md v0) — `POST /progress`. `token` is whatever
   * {@link listCatalogs} already accepts for this provider (a worker session id OR
   * an OIDC access token, sent the same `X-Session` way — the Worker dispatches on
   * `.`-count) — this is the provider's own student data, so it's always
   * account-attributed, never anonymous like `submitFeedback`/`submitReport`.
   */
  submitProgress(payload: ProgressSubmission, token: string): Promise<ProgressSubmissionResponse> {
    return this.post<ProgressSubmissionResponse>("/progress", payload, token);
  }

  /**
   * Upsert a batch of the caller's own media/narration resume positions to a provider
   * (docs/304-PROVIDER-PROGRESS-SUBMISSION.md Part B v0) — `POST /media-progress`.
   * `token` is the SAME session-or-OIDC-access-token {@link listCatalogs}/
   * {@link submitProgress} already accept. Upserted server-side by `(userId, refKey)`
   * with LAST-WRITER-WINS-on-`updatedAt` semantics — a stale/tied push is a silent
   * no-op, never a clobber (see the response's `accepted`, which counts well-formed
   * items ATTEMPTED, not necessarily each a winning write).
   */
  putMediaProgress(token: string, items: SyncedMediaProgress[]): Promise<MediaProgressPutResponse> {
    return this.post<MediaProgressPutResponse>("/media-progress", { items }, token);
  }

  /**
   * The caller's own media/narration resume positions across every one of their
   * devices, newest-`updatedAt`-first (docs/304 Part B v0) — `GET /media-progress`,
   * the bidirectional half {@link submitProgress}'s `/progress` never needed. `token`
   * is the SAME session-or-OIDC-access-token acceptance {@link listCatalogs}/
   * {@link submitProgress} already accept (sent as `Authorization: Bearer`, which the
   * Worker's `requireSessionOrOidcAccessToken` treats identically to `X-Session`).
   * `opts.since` (epoch ms) narrows to rows updated strictly after that cutoff;
   * `opts.ref` (a `mediaRefKey`) narrows to one lesson's media. Both may be combined.
   */
  getMediaProgress(
    token: string,
    opts: { since?: number; ref?: string } = {},
  ): Promise<MediaProgressListResponse> {
    const params = new URLSearchParams();
    if (opts.since != null) params.set("since", String(opts.since));
    if (opts.ref) params.set("ref", opts.ref);
    const qs = params.toString();
    return this.authed<MediaProgressListResponse>("GET", `/media-progress${qs ? `?${qs}` : ""}`, token);
  }

  /**
   * Push a batch of the caller's own unified user-state envelopes to a provider
   * (docs/361-UNIFIED-USER-STATE-SCHEMA-AND-SYNC.md v1) — `POST /user-state`, the
   * general sibling of {@link putMediaProgress} for ANY `UserStateDomain`. `token` is
   * the SAME session-or-OIDC-access-token acceptance {@link listCatalogs}/
   * {@link putMediaProgress} already accept. Upserted server-side by `(userId, domain,
   * refKey, profileId)` with REGISTER-class last-writer-wins semantics (docs/361
   * §4.1's `mergeRegister`) — a stale/tied push is a silent no-op. See
   * {@link UserStatePostResponse}'s own doc for why the response echoes back
   * `records`.
   */
  pushUserState(token: string, items: UserStateEnvelope[]): Promise<UserStatePostResponse> {
    return this.post<UserStatePostResponse>("/user-state", { items }, token);
  }

  /**
   * The caller's own unified user-state envelopes across every one of their devices,
   * newest-`updatedAt`-first (docs/361 v1) — `GET /user-state`, the general sibling
   * of {@link getMediaProgress}. `token` is the SAME acceptance
   * {@link listCatalogs}/{@link pushUserState} already accept. `opts.domains`
   * narrows to specific `UserStateDomain`s (omit for every domain the provider
   * carries); `opts.since` (epoch ms) narrows to rows updated strictly after that
   * cutoff; `opts.refs` narrows to specific `userStateRefKey` values. Any combination
   * may be supplied.
   */
  pullUserState(
    token: string,
    opts: { domains?: UserStateDomain[]; since?: number; refs?: string[] } = {},
  ): Promise<UserStateGetResponse> {
    const params = new URLSearchParams();
    if (opts.domains && opts.domains.length > 0) params.set("domains", opts.domains.join(","));
    if (opts.since != null) params.set("since", String(opts.since));
    if (opts.refs && opts.refs.length > 0) params.set("refs", opts.refs.join(","));
    const qs = params.toString();
    return this.authed<UserStateGetResponse>("GET", `/user-state${qs ? `?${qs}` : ""}`, token);
  }

  /**
   * Submit a batch of buffered, aggregate usage-counter deltas to a provider
   * (docs/310-PROVIDER-USAGE-ANALYTICS.md v0) — `POST /usage`. Same auth acceptance as
   * {@link submitProgress}/{@link listCatalogs} (a worker session id OR an OIDC access
   * token, `X-Session`). The provider may silently accept fewer items than sent
   * (`accepted < items.length`) when the account hasn't consented to the docs/300
   * tracking disclosure — never a rejected request, see `UsageSubmissionResponse`'s doc.
   */
  submitUsage(payload: UsageSubmission, token: string): Promise<UsageSubmissionResponse> {
    return this.post<UsageSubmissionResponse>("/usage", payload, token);
  }

  /**
   * `POST /accept-terms` (docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md v0 §5.2) —
   * the server-side "who accepted which version" compliance record for a
   * provider/catalog/pack terms-of-usage acceptance the app already gates on
   * LOCALLY (`AcceptedTermsStore`, docs/297) — never itself the app-side gate,
   * purely the provider's own audit trail. `scope` is whatever `providerTermsScope`/
   * `catalogTermsScope`/`packTermsScope` (`@equipped/core/legal/catalogTerms.ts`)
   * already computed. `token` is the SAME session-or-OIDC-access-token
   * {@link listCatalogs}/{@link submitProgress}/{@link submitUsage} already accept.
   * Append-only server-side — a repeat call with the same `(scope, version)` is a
   * harmless idempotent no-op.
   */
  acceptTerms(scope: string, version: number, token: string): Promise<{ ok: boolean }> {
    return this.post<{ ok: boolean }>("/accept-terms", { scope, version }, token);
  }

  /**
   * `POST /device/approve` (docs/090-TVOS-DEVICE-PAIRING-BACKEND.md, RFC 8628) — an
   * already-signed-in provider account approves (or denies) a device's short pairing
   * `userCode`, binding the eventual TV/device session to THIS account. `token` is the
   * same session-or-OIDC-access-token {@link listCatalogs}/{@link submitProgress}
   * already accept (sent as `Authorization: Bearer`, which the Worker's
   * `requireSessionOrOidcAccessToken` treats identically to `X-Session`). Throws
   * `BackendError(404)` when this provider hasn't deployed device pairing
   * (`DEVICE_PAIRING_ENABLED` unset) and `BackendError(400)` for an unknown/expired/
   * already-decided code (RFC 8628 deliberately makes those indistinguishable).
   */
  approveDevice(
    token: string,
    userCode: string,
    action: "approve" | "deny" = "approve",
  ): Promise<{ ok: boolean }> {
    return this.authed<{ ok: boolean }>("POST", "/device/approve", token, { userCode, action });
  }

  /**
   * Resolve the current user (+ moderation status, docs/105 v1) from a worker
   * session id — used to restore/validate a session on launch (throws
   * BackendError(401) if the session is invalid).
   */
  async me(sessionId: string): Promise<MeResult> {
    const res = await this.fetchFn(`${this.baseUrl}/auth/me`, {
      method: "GET",
      headers: { Authorization: `Bearer ${sessionId}` },
    });
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      user?: AppleSignInResult["user"];
      moderation?: UserModeration | null;
    };
    if (!res.ok || !data.user) {
      throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    }
    return { ...data.user, moderation: data.moderation ?? null };
  }

  // ── Notices inbox (docs/105-ADMIN-MODERATION-AND-MEMBERSHIP-ENFORCEMENT.md v1) ─
  // Session-scoped — a Notice is keyed to a specific worker user id, so this always
  // returns/marks only the caller's own notices (enforced server-side too).

  /** The signed-in user's own notices, newest first. */
  async notices(sessionId: string): Promise<Notice[]> {
    const res = await this.fetchFn(`${this.baseUrl}/notices`, {
      method: "GET",
      headers: { Authorization: `Bearer ${sessionId}` },
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string; notices?: Notice[] };
    if (!res.ok) throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    return data.notices ?? [];
  }

  /** Mark one of the caller's own notices read. Throws 404 if it's missing or not theirs. */
  markNoticeRead(sessionId: string, id: string): Promise<Notice> {
    return this.authed<{ notice: Notice }>("POST", "/notices/read", sessionId, { id }).then((d) => d.notice);
  }

  /**
   * The signed-in user's OWN reports, newest first (docs/282-REPORT-ANONYMITY-AND-
   * MY-REPORTS.md v0 — the "My Reports" status page). Session-scoped exactly like
   * {@link notices} — there is no email-keyed equivalent (see the doc's §5); an
   * anonymous report is structurally unreachable through this method.
   */
  async myReports(sessionId: string): Promise<AdminReport[]> {
    const res = await this.fetchFn(`${this.baseUrl}/reports`, {
      method: "GET",
      headers: { Authorization: `Bearer ${sessionId}` },
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string; reports?: AdminReport[] };
    if (!res.ok) throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    return data.reports ?? [];
  }

  /**
   * Permanently delete the signed-in user's account and its server-side data
   * (docs/106-IN-APP-LEGAL-PAGE.md v1, Apple 5.1.1(v)) — sessions, grants, and the
   * personal link on feedback/reports (those rows are kept, anonymized, for
   * moderation history). Throws `BackendError(401)` if the session is invalid. The
   * caller is responsible for clearing its own local session state afterward —
   * this only deletes what lives on the server.
   */
  deleteAccount(sessionId: string): Promise<{ ok: boolean }> {
    return this.authed<{ ok: boolean }>("POST", "/auth/delete", sessionId);
  }

  /**
   * Initiate TOTP 2FA setup for the currently signed-in account (docs/342).
   * Returns a secret (Base32) and an `otpauth://` URI to encode as a QR code.
   */
  totpBegin(token: string): Promise<{ secret_base32: string; otpauth_uri: string }> {
    return this.authed<{ secret_base32: string; otpauth_uri: string }>("POST", "/auth/2fa/totp/begin", token);
  }

  /**
   * Confirm TOTP 2FA setup by providing a valid 6-digit verification code.
   * Returns single-use recovery codes on success.
   */
  totpConfirm(token: string, code: string): Promise<{ recovery_codes: string[] }> {
    return this.authed<{ recovery_codes: string[] }>("POST", "/auth/2fa/totp/confirm", token, { code });
  }

  /**
   * Disable 2FA for the currently signed-in account using a current TOTP code or password.
   */
  disable2fa(token: string, input: { code?: string; password?: string } = {}): Promise<{ ok: true }> {
    return this.authed<{ ok: true }>("POST", "/auth/2fa/disable", token, input);
  }

  /**
   * The signed-in user's OWN connected devices, newest `lastSeenAt` first
   * (docs/326-MY-CONNECTED-DEVICES.md v0 — the "Connected devices" section on
   * Provider Detail) — `GET /devices`, the self-scoped consumer analog of the
   * admin-only {@link adminUserDevices}. `token` is the SAME session-or-OIDC-
   * access-token acceptance {@link listCatalogs}/{@link submitProgress}/
   * {@link submitUsage} already accept (sent as `Authorization: Bearer`, which the
   * Worker's `requireSessionOrOidcAccessToken` treats identically to `X-Session`).
   */
  myDevices(token: string): Promise<AdminDevice[]> {
    return this.authed<{ devices: AdminDevice[] }>("GET", "/devices", token).then((d) => d.devices);
  }

  // ── Admin API (authorized by the admin's worker session bearer token) ───────
  /** The admin landing dashboard's one read model (`GET /admin/overview`,
   * docs/340 v1 §4.1) — see {@link AdminOverview}. */
  adminOverview(token: string): Promise<AdminOverview> {
    return this.authed<{ overview: AdminOverview }>("GET", "/admin/overview", token).then((d) => d.overview);
  }
  /** By default excludes soft-deleted users; pass `{ includeDeleted: true }` for
   * the People-tab "Show removed" view (docs/013 overhaul). */
  adminUsers(token: string, opts?: { includeDeleted?: boolean }): Promise<AdminUser[]> {
    const q = opts?.includeDeleted ? "/admin/users?includeDeleted=1" : "/admin/users";
    return this.authed<{ users: AdminUser[] }>("GET", q, token).then((d) => d.users);
  }
  /**
   * The server-side cursor-paginated + filtered counterpart to {@link adminUsers}
   * (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md v1 §4) — `GET /admin/users?limit=`.
   * `cursor` (from a prior call's own `nextCursor`) resumes after that page;
   * omit it for the first page. `nextCursor` is `null` on the last page. A
   * SEPARATE method from {@link adminUsers} (not an overload) so every
   * existing `adminUsers()` caller — `apps/admin`, the dormant `apps/admin-web`
   * — is completely unaffected; only a caller that opts into pagination sees it.
   */
  adminUsersPage(
    token: string,
    opts: { limit: number; cursor?: string | null; includeDeleted?: boolean; q?: string; role?: string },
  ): Promise<{ users: AdminUser[]; nextCursor: string | null }> {
    const params = new URLSearchParams({ limit: String(opts.limit) });
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.includeDeleted) params.set("includeDeleted", "1");
    if (opts.q) params.set("q", opts.q);
    if (opts.role) params.set("role", opts.role);
    return this.authed<{ users: AdminUser[]; nextCursor: string | null }>(
      "GET",
      `/admin/users?${params.toString()}`,
      token,
    );
  }
  adminSetRole(token: string, userId: string, role: string): Promise<AdminUser> {
    return this.authed<{ user: AdminUser }>("POST", "/admin/users/role", token, { userId, role }).then((d) => d.user);
  }
  /** Soft-delete a user — revokes their sessions + hides them from the default
   * `adminUsers()` list; reversible via {@link adminRestoreUser}. Distinct from
   * docs/105 ban/suspend and from the user's own permanent `deleteAccount`. */
  adminDeleteUser(token: string, userId: string): Promise<AdminUser> {
    return this.authed<{ user: AdminUser }>("POST", "/admin/users/delete", token, { userId }).then((d) => d.user);
  }
  /** Clears a soft delete, restoring the user to the default `adminUsers()` list. */
  adminRestoreUser(token: string, userId: string): Promise<AdminUser> {
    return this.authed<{ user: AdminUser }>("POST", "/admin/users/restore", token, { userId }).then((d) => d.user);
  }
  /**
   * Sets/resets a user's password (docs/323-ADMIN-CREDENTIAL-MANAGEMENT.md v0 Fix
   * A) — server-hashed, audited, never logged. Also **reactivates** a soft-deleted
   * account (clears `deletedAt`) and **claims** a docs/286-provisioned account the
   * first time — the un-stick for a user who was added but never got credentials.
   * `password` must be 8-200 characters or the server 400s. Every existing session
   * for the user is revoked.
   */
  adminSetUserPassword(token: string, userId: string, password: string): Promise<AdminUser> {
    return this.authed<{ user: AdminUser }>("POST", "/admin/users/set-password", token, {
      userId,
      password,
    }).then((d) => d.user);
  }
  /**
   * **Permanently** deletes a user (docs/323 v0 Fix B — reuses the same server
   * path as the user's own `deleteAccount`) — removes the row, sessions, and
   * grants outright and frees the email for reuse. Distinct from the reversible
   * {@link adminDeleteUser} soft delete above; there is no undo.
   */
  adminHardDeleteUser(token: string, userId: string): Promise<{ ok: boolean }> {
    return this.authed<{ ok: boolean }>("POST", "/admin/users/hard-delete", token, { userId });
  }
  /**
   * Pre-provisions a real, **granted** account with NO invite (docs/286-ADMIN-ADD-
   * USER-WITHOUT-INVITE.md v1, Idea 1) — an admin "just adds a user" from the
   * People tab. The created account is credential-less (`provisionedAt` set,
   * `claimedAt: null`) until that person's first VERIFIED-identity sign-in (Sign
   * in with Apple's email-link adoption today) claims it — no invite code, no
   * password. Throws `BackendError(409)` if `email` is already registered.
   */
  adminCreateUser(
    token: string,
    body: { email: string; firstName?: string; lastName?: string; grants?: GrantSpec[] },
  ): Promise<{ user: AdminUser; grants: AdminGrant[] }> {
    return this.authed<{ user: AdminUser; grants: AdminGrant[] }>("POST", "/admin/users", token, body);
  }
  /**
   * Bulk "which of these emails already have accounts" (docs/359-BULK-EMAIL-
   * LOOKUP-AND-ACCESS-GIFTING.md v0) — read-only, per-provider only. `emails`
   * is trimmed/lowercased/deduplicated server-side; a malformed address is
   * silently dropped rather than rejecting the whole call. Throws
   * `BackendError(400)` for an empty (post-normalization) list or one over the
   * server's per-call cap (chunk a larger contacts file across multiple calls).
   */
  adminUsersLookup(token: string, emails: string[]): Promise<AdminUserLookupResult[]> {
    return this.authed<{ results: AdminUserLookupResult[] }>("POST", "/admin/users/lookup", token, {
      emails,
    }).then((d) => d.results);
  }
  /**
   * Gifts the SAME set of content-catalog `grants` (kind `pack`|`catalog`) to
   * every matched `userIds` — the bulk form of {@link adminAddGrant}, reusing
   * the exact same idempotent-upsert write per (user × spec). **Silent** (no
   * Notice/email sent to the gifted account — the owner's explicit "not a
   * notification" ask, docs/359 v0 §2) and audited server-side. A
   * membership-entitlement gift is NOT what this does — content grants only;
   * see the doc's §5 for why.
   */
  adminGrantBulk(token: string, userIds: string[], grants: GrantSpec[]): Promise<AdminBulkGrantResult[]> {
    return this.authed<{ results: AdminBulkGrantResult[] }>("POST", "/admin/users/grant-bulk", token, {
      userIds,
      grants,
    }).then((d) => d.results);
  }
  adminInvites(token: string): Promise<AdminInvite[]> {
    return this.authed<{ invites: AdminInvite[] }>("GET", "/admin/invites", token).then((d) => d.invites);
  }
  /**
   * Mints an invite; when `send:true` and `email` yields ≥1 valid address, also
   * emails it immediately (docs/273-ADMIN-INVITE-EMAILS.md v0) — `deliveries` is
   * present in the result exactly then, `undefined` for the (byte-for-byte
   * backward-compatible) mint-only call.
   */
  adminCreateInvite(
    token: string,
    body: {
      code?: string;
      maxUses?: number | null;
      days?: number | null;
      grants?: GrantSpec[];
      note?: string | null;
      message?: string | null;
      email?: string | string[];
      send?: boolean;
    },
  ): Promise<{ invite: AdminInvite; deliveries?: AdminInviteDelivery[] }> {
    return this.authed<{ invite: AdminInvite; deliveries?: AdminInviteDelivery[] }>(
      "POST",
      "/admin/invites",
      token,
      body,
    );
  }
  adminRevokeInvite(token: string, code: string, active = false): Promise<AdminInvite> {
    return this.authed<{ invite: AdminInvite }>("POST", "/admin/invites/revoke", token, { code, active }).then((d) => d.invite);
  }
  /** Emails an EXISTING invite code to one or more recipients — resend, or "add more
   * people" when `maxUses` allows it (docs/273 v0). Never mints a new invite. */
  adminSendInvite(token: string, code: string, email: string | string[]): Promise<AdminInviteDelivery[]> {
    return this.authed<{ deliveries: AdminInviteDelivery[] }>("POST", "/admin/invites/send", token, {
      code,
      email,
    }).then((d) => d.deliveries);
  }
  /** Per-recipient email delivery status for one invite code, newest-first (docs/273 v0). */
  adminInviteDeliveries(token: string, code: string): Promise<AdminInviteDelivery[]> {
    const q = `/admin/invites/deliveries?code=${encodeURIComponent(code)}`;
    return this.authed<{ deliveries: AdminInviteDelivery[] }>("GET", q, token).then((d) => d.deliveries);
  }
  /**
   * Patch an existing invite's `grants`/`maxUses`/expiry/`note`/`message` — `active`
   * stays on {@link adminRevokeInvite}, `usedCount`/`code`/`createdAt` are never
   * touched. A `patch` field that's omitted is left unchanged server-side; a field
   * that IS present (even `null` or an empty `grants` array) is set to that value.
   * Editing an invite's grants affects only future redemptions, not accounts
   * already created from it. `note`/`message` are docs/290's admin-only vs.
   * user-facing description fields.
   */
  adminUpdateInvite(
    token: string,
    code: string,
    patch: {
      grants?: GrantSpec[];
      maxUses?: number | null;
      days?: number | null;
      expiresAt?: number | null;
      note?: string | null;
      message?: string | null;
    },
  ): Promise<AdminInvite> {
    return this.authed<{ invite: AdminInvite }>("POST", "/admin/invites/update", token, { code, ...patch }).then(
      (d) => d.invite,
    );
  }
  /** The full invite-attempt audit trail, newest first — success AND failure (docs/263 §7). */
  adminInviteAttempts(token: string): Promise<AdminInviteAttempt[]> {
    return this.authed<{ attempts: AdminInviteAttempt[] }>("GET", "/admin/invite-attempts", token).then(
      (d) => d.attempts,
    );
  }
  adminGrants(token: string, userId: string): Promise<AdminGrant[]> {
    const q = `/admin/grants?userId=${encodeURIComponent(userId)}`;
    return this.authed<{ grants: AdminGrant[] }>("GET", q, token).then((d) => d.grants);
  }
  /** Who has access to a given pack/catalog ref (the inverse of {@link adminGrants}). */
  adminGrantsByRef(token: string, kind: GrantSpec["kind"], ref: string): Promise<AdminGrant[]> {
    const q = `/admin/grants?kind=${encodeURIComponent(kind)}&ref=${encodeURIComponent(ref)}`;
    return this.authed<{ grants: AdminGrant[] }>("GET", q, token).then((d) => d.grants);
  }
  /**
   * `expiresAt` is optional (docs/306-CATALOG-ACCESS-PROTECTION-AND-TIMED-GRANTS.md
   * v0) — omitted (the default, backward-compatible) means "never expires," matching
   * every pre-306 caller unchanged; passed as epoch seconds, it time-boxes the grant
   * and the content token minted for it (`worker/src/catalogs.ts`
   * `clampTtlToExpiry`). Re-granting the same `(userId,kind,ref)` upserts — a later
   * call with a new `expiresAt` extends/changes the loan rather than duplicating.
   */
  adminAddGrant(
    token: string,
    userId: string,
    kind: GrantSpec["kind"],
    ref: string,
    expiresAt?: number | null,
  ): Promise<AdminGrant[]> {
    const body: { userId: string; kind: GrantSpec["kind"]; ref: string; expiresAt?: number | null } = {
      userId,
      kind,
      ref,
    };
    if (expiresAt !== undefined) body.expiresAt = expiresAt;
    return this.authed<{ grants: AdminGrant[] }>("POST", "/admin/grants", token, body).then((d) => d.grants);
  }
  adminRevokeGrant(token: string, userId: string, kind: GrantSpec["kind"], ref: string): Promise<AdminGrant[]> {
    return this.authed<{ grants: AdminGrant[] }>("POST", "/admin/grants/revoke", token, { userId, kind, ref }).then((d) => d.grants);
  }
  adminFeedback(token: string): Promise<AdminFeedback[]> {
    return this.authed<{ feedback: AdminFeedback[] }>("GET", "/admin/feedback", token).then((d) => d.feedback);
  }
  adminReports(token: string): Promise<AdminReport[]> {
    return this.authed<{ reports: AdminReport[] }>("GET", "/admin/reports", token).then((d) => d.reports);
  }
  // ── Admin manifest builder (docs/168-ADMIN-MANIFEST-BUILDER.md v0) ─
  /** Browse one level of the gated content bucket for the manifest builder. */
  adminListContent(token: string, prefix = "", bucket?: AdminFileBucket): Promise<AdminContentListing> {
    const q = `/admin/content/list?prefix=${encodeURIComponent(prefix)}${bucket ? `&bucket=${bucket}` : ""}`;
    return this.authed<AdminContentListing>("GET", q, token);
  }
  adminPutFile(token: string, key: string, file: Blob, bucket: AdminFileBucket = "gated"): Promise<AdminStoredFile> {
    return this.binaryAuthed<AdminStoredFile>(
      "POST",
      `/admin/files?key=${encodeURIComponent(key)}&bucket=${bucket}`,
      token,
      file,
    );
  }
  adminCreateMultipart(
    token: string,
    input: { key: string; bucket?: AdminFileBucket; size: number; contentType?: string; filename?: string },
  ): Promise<AdminMultipartUpload> {
    return this.authed<AdminMultipartUpload>("POST", "/admin/files/multipart", token, input);
  }
  adminPutMultipartPart(token: string, uploadId: string, partNumber: number, part: Blob): Promise<void> {
    return this.binaryAuthed<unknown>(
      "PUT",
      `/admin/files/multipart/${encodeURIComponent(uploadId)}/${partNumber}`,
      token,
      part,
    ).then(() => undefined);
  }
  adminCompleteMultipart(token: string, uploadId: string): Promise<AdminStoredFile> {
    return this.authed<AdminStoredFile>(
      "POST",
      `/admin/files/multipart/${encodeURIComponent(uploadId)}/complete`,
      token,
    );
  }
  adminDeleteFile(token: string, key: string, bucket: AdminFileBucket = "gated"): Promise<void> {
    return this.authed<unknown>("DELETE", `/admin/files/${encodeURIComponent(key)}?bucket=${bucket}`, token)
      .then(() => undefined);
  }
  /** The admin-authored manifest index (ids/titles/timestamps, no bodies). */
  adminListManifests(token: string): Promise<AdminManifest[]> {
    return this.authed<{ manifests: AdminManifest[] }>("GET", "/admin/manifests", token).then((d) => d.manifests);
  }
  /** Validate + store a manifest (gated R2 object + D1 index); returns the index row. */
  adminSaveManifest(token: string, manifest: unknown): Promise<AdminManifest> {
    return this.authed<{ manifest: AdminManifest }>("POST", "/admin/manifests", token, { manifest }).then(
      (d) => d.manifest,
    );
  }
  // ── Manifest/catalog EDITOR (docs/343-ADMIN-MANIFEST-BUILDER-REDESIGN.md v0) —
  // "load an existing pack to edit" + the CATALOG-document CRUD sibling of the
  // manifest builder above ─────────────────────────────────────────────────
  /** The full body of one already-saved manifest ("load an existing pack to edit"). */
  adminGetManifest(token: string, id: string): Promise<AdminManifestDetail> {
    return this.authed<{ manifest: AdminManifestDetail }>(
      "GET",
      `/admin/manifests/${encodeURIComponent(id)}`,
      token,
    ).then((d) => d.manifest);
  }
  /** The admin-authored catalog-document index (ids/titles/timestamps, no bodies) —
   *  the catalog-document sibling of `adminListManifests`. */
  adminListCatalogDocs(token: string): Promise<AdminCatalogDoc[]> {
    return this.authed<{ catalogDocs: AdminCatalogDoc[] }>("GET", "/admin/catalog-docs", token).then(
      (d) => d.catalogDocs,
    );
  }
  /** Validate + store a catalog document (gated R2 object + D1 index); returns the
   *  index row — the catalog-document sibling of `adminSaveManifest`. */
  adminSaveCatalogDoc(token: string, catalog: unknown): Promise<AdminCatalogDoc> {
    return this.authed<{ catalogDoc: AdminCatalogDoc }>("POST", "/admin/catalog-docs", token, { catalog }).then(
      (d) => d.catalogDoc,
    );
  }
  /** The full body of one already-saved catalog document. */
  adminGetCatalogDoc(token: string, id: string): Promise<AdminCatalogDocDetail> {
    return this.authed<{ catalogDoc: AdminCatalogDocDetail }>(
      "GET",
      `/admin/catalog-docs/${encodeURIComponent(id)}`,
      token,
    ).then((d) => d.catalogDoc);
  }
  // ── Provider device/usage analytics + provider config (docs/300-PROVIDER-
  // DEVICE-ANALYTICS-AND-DISCLOSURE.md v0) — the admin Devices + Provider
  // Settings tabs ────────────────────────────────────────────────────────
  /** Aggregate device/usage stats (`GET /admin/devices/stats`); `activeDays`
   * overrides the server default "active in the last N days" window. */
  adminDeviceStats(token: string, opts?: { activeDays?: number }): Promise<AdminDeviceStats> {
    const q =
      opts?.activeDays != null
        ? `/admin/devices/stats?activeDays=${encodeURIComponent(String(opts.activeDays))}`
        : "/admin/devices/stats";
    return this.authed<{ stats: AdminDeviceStats }>("GET", q, token).then((d) => d.stats);
  }
  /** Every distinct device one account has connected with, newest-seen first
   * (`GET /admin/users/devices?userId=`, docs/300 §6) — the per-user detail
   * behind the aggregate `perAccount` count, for the admin People page. */
  adminUserDevices(token: string, userId: string): Promise<AdminDevice[]> {
    const q = `/admin/users/devices?userId=${encodeURIComponent(userId)}`;
    return this.authed<{ devices: AdminDevice[] }>("GET", q, token).then((d) => d.devices);
  }
  /** EVERY recorded device across every account, newest-seen first
   * (`GET /admin/devices`, docs/300 v1) — the raw per-device rows behind
   * {@link adminDeviceStats}'s aggregates, backing the admin Devices page's
   * "Version" column + filter. `opts.appVersion` narrows server-side to
   * devices whose most-recently-seen app version matches exactly; omit for
   * every device. */
  adminDevices(token: string, opts?: { appVersion?: string }): Promise<AdminDevice[]> {
    const q = opts?.appVersion
      ? `/admin/devices?appVersion=${encodeURIComponent(opts.appVersion)}`
      : "/admin/devices";
    return this.authed<{ devices: AdminDevice[] }>("GET", q, token).then((d) => d.devices);
  }
  /** The stored provider-config row (or `null`) + the env-var fallback each field
   * would resolve to. */
  adminProviderConfig(token: string): Promise<AdminProviderConfigResult> {
    return this.authed<AdminProviderConfigResult>("GET", "/admin/provider-config", token);
  }
  /** Full replace-by-upsert of the provider-config singleton — realizes docs/263's
   * "edit my provider name from the admin site, along with other configs." */
  adminSetProviderConfig(token: string, patch: AdminProviderConfigPatch): Promise<AdminProviderConfig> {
    return this.authed<{ config: AdminProviderConfig }>("PUT", "/admin/provider-config", token, patch).then(
      (d) => d.config,
    );
  }
  /** Per-metric usage totals (listens/exports/prints/…) + terms-accepted-by-version
   * (`GET /admin/usage/stats`, docs/310-PROVIDER-USAGE-ANALYTICS.md v0). */
  adminUsageStats(token: string): Promise<AdminUsageStats> {
    return this.authed<{ stats: AdminUsageStats }>("GET", "/admin/usage/stats", token).then((d) => d.stats);
  }

  // ── Admin alerts + monitoring (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0) ──
  /** Every configured alert rule (no ordering guaranteed by the server). */
  adminAlertRules(token: string): Promise<AdminAlertRule[]> {
    return this.authed<{ rules: AdminAlertRule[] }>("GET", "/admin/alert-rules", token).then((d) => d.rules);
  }
  /** Create (no `id`) or update-by-`id` (upsert) an alert rule — the Worker validates
   * every field server-side. */
  adminSaveAlertRule(token: string, input: AdminAlertRuleInput): Promise<AdminAlertRule> {
    return this.authed<{ rule: AdminAlertRule }>("POST", "/admin/alert-rules", token, input).then((d) => d.rule);
  }
  /** No-op (not an error) for an unknown id. */
  adminDeleteAlertRule(token: string, id: string): Promise<{ ok: boolean }> {
    return this.authed<{ ok: boolean }>("DELETE", `/admin/alert-rules?id=${encodeURIComponent(id)}`, token);
  }
  /** Recent fired/recovered alert episodes, newest-`openedAt`-first. */
  adminAlertEvents(token: string, opts?: { limit?: number }): Promise<AdminAlertEvent[]> {
    const q = opts?.limit != null ? `/admin/alert-events?limit=${opts.limit}` : "/admin/alert-events";
    return this.authed<{ events: AdminAlertEvent[] }>("GET", q, token).then((d) => d.events);
  }
  /** The live status dashboard's one read model — current metric headroom + up/down. */
  adminMonitoring(token: string): Promise<AdminMonitoring> {
    return this.authed<{ monitoring: AdminMonitoring }>("GET", "/admin/monitoring", token).then((d) => d.monitoring);
  }
  /** The auto per-client Cloudflare usage/cost estimate — `{available:false}` when
   * Cloudflare Analytics credentials aren't configured on this deployment. */
  adminCost(token: string): Promise<AdminCost> {
    return this.authed<{ cost: AdminCost }>("GET", "/admin/cost", token).then((d) => d.cost);
  }
  /** Who accepted which provider/catalog/pack terms version, newest-first
   * (`GET /admin/terms-acceptances`, docs/312-PROVIDER-LEGAL-AND-TERMS-EDITING.md
   * v0 §5.3) — optionally narrowed to one `scope` and/or `version`. */
  adminTermsAcceptances(
    token: string,
    opts?: { scope?: string; version?: number },
  ): Promise<AdminTermsAcceptance[]> {
    const params = new URLSearchParams();
    if (opts?.scope) params.set("scope", opts.scope);
    if (opts?.version != null) params.set("version", String(opts.version));
    const q = params.toString();
    return this.authed<{ acceptances: AdminTermsAcceptance[] }>(
      "GET",
      `/admin/terms-acceptances${q ? `?${q}` : ""}`,
      token,
    ).then((d) => d.acceptances);
  }
  /** One account's merged, newest-first submissions/activity feed
   * (`GET /admin/users/history?userId=`, docs/311-ADMIN-USER-HISTORY.md v0) —
   * invite attempts, reports, feedback, grants applied, notices, admin actions,
   * and account lifecycle, all in one call. `types` narrows to a subset of
   * `AdminHistoryEvent["type"]`; `limit` caps the merged result (server default
   * 200, hard ceiling 500). */
  adminUserHistory(
    token: string,
    userId: string,
    opts?: { types?: AdminHistoryEvent["type"][]; limit?: number },
  ): Promise<AdminHistoryEvent[]> {
    const params = new URLSearchParams({ userId });
    if (opts?.types?.length) params.set("types", opts.types.join(","));
    if (opts?.limit != null) params.set("limit", String(opts.limit));
    return this.authed<{ events: AdminHistoryEvent[] }>(
      "GET",
      `/admin/users/history?${params.toString()}`,
      token,
    ).then((d) => d.events);
  }

  /**
   * One moderation decision on a filed report — dismiss it, or take the reported
   * content down (docs/105 v0). Returns the updated report.
   */
  adminReportAction(
    token: string,
    body: { reportId: string; decision: ReportDecision; reason?: string; note?: string },
  ): Promise<AdminReport> {
    return this.authed<{ report: AdminReport }>("POST", "/admin/reports/action", token, body).then(
      (d) => d.report,
    );
  }
  /** Record a standalone content takedown (docs/105 v0), outside a filed report. */
  adminTakedownContent(token: string, ref: string, reason?: string): Promise<AdminTakedown> {
    return this.authed<{ takedown: AdminTakedown }>("POST", "/admin/content/takedown", token, {
      ref,
      reason,
    }).then((d) => d.takedown);
  }
  /** The append-only moderation/admin action log, newest first (docs/105 v0). */
  adminAudit(token: string): Promise<AuditEvent[]> {
    return this.authed<{ audit: AuditEvent[] }>("GET", "/admin/audit", token).then((d) => d.audit);
  }

  // ── Admin page-access tracking (docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0) ─
  /** Fire one page-access beacon (`POST /admin/track`) — fire-and-forget from
   * the caller's perspective (no return payload beyond `{ok:true}`, so this
   * resolves to `void`; a network failure should be swallowed by the
   * caller, never blocking navigation). Records nothing (still a 2xx) when
   * the provider has `adminAccessTrackingEnabled: false`; 400s on an
   * unrecognized `event`/`path` (a bounded server-side allow-list). */
  adminTrack(token: string, body: { event: "page_view" | "action_view"; path: string }): Promise<void> {
    return this.authed<{ ok: boolean }>("POST", "/admin/track", token, body).then(() => undefined);
  }
  /** The page-access aggregate (`GET /admin/access/stats`), windowed to the
   * last `opts.days` days (server default 30). */
  adminAccessStats(token: string, opts?: { days?: number }): Promise<AdminAccessStats> {
    const q = opts?.days != null ? `/admin/access/stats?days=${encodeURIComponent(String(opts.days))}` : "/admin/access/stats";
    return this.authed<{ stats: AdminAccessStats }>("GET", q, token).then((d) => d.stats);
  }

  // ── Account/membership moderation (docs/105-ADMIN-MODERATION-AND-MEMBERSHIP-ENFORCEMENT.md v1) ─

  /** Create a standalone notice for a user (e.g. a plain warning), independent of a report. */
  adminCreateNotice(
    token: string,
    body: { userId: string; kind: NoticeKind; body: string; ref?: string },
  ): Promise<Notice> {
    return this.authed<{ notice: Notice }>("POST", "/admin/notices", token, body).then((d) => d.notice);
  }
  /**
   * Every notice, newest first, optionally filtered to one account — the admin
   * Notices tab's list (docs/301-ADMIN-NOTICES-AND-REMOTE-PUSH.md v0). Distinct from
   * {@link notices}, which is self-scoped to the signed-in caller.
   */
  adminNotices(token: string, opts?: { userId?: string }): Promise<Notice[]> {
    const q = opts?.userId ? `/admin/notices?userId=${encodeURIComponent(opts.userId)}` : "/admin/notices";
    return this.authed<{ notices: Notice[] }>("GET", q, token).then((d) => d.notices);
  }
  /**
   * Send a notice to a specific account, an explicit set, or every account
   * (docs/301 v0) — the admin Notices tab's compose action. Reuses the same
   * `POST /admin/notices` endpoint {@link adminCreateNotice} does; exactly one of
   * `userId`/`userIds`/`broadcast` should be set (the worker 400s otherwise).
   */
  adminSendNotice(
    token: string,
    body: { userId?: string; userIds?: string[]; broadcast?: boolean; kind: NoticeKind; body: string; ref?: string },
  ): Promise<{ notices: Notice[]; count: number }> {
    return this.authed<{ notices: Notice[]; count: number }>("POST", "/admin/notices", token, body);
  }
  /**
   * Every submitted progress row, newest-updated-first, optionally filtered to
   * one account (docs/304-PROVIDER-PROGRESS-SUBMISSION.md v0) — the admin
   * "which student completed what" view.
   */
  adminProgress(token: string, opts?: { userId?: string }): Promise<AdminProgressRow[]> {
    const q = opts?.userId ? `/admin/progress?userId=${encodeURIComponent(opts.userId)}` : "/admin/progress";
    return this.authed<{ progress: AdminProgressRow[] }>("GET", q, token).then((d) => d.progress);
  }
  /** Suspend a user — read-only until `until` (epoch seconds; omit for indefinite). */
  adminSuspend(
    token: string,
    body: { userId: string; reason?: string; until?: number | null },
  ): Promise<UserModeration> {
    return this.authed<{ moderation: UserModeration }>("POST", "/admin/membership/suspend", token, body).then(
      (d) => d.moderation,
    );
  }
  /** Clear a suspension/warning back to `"active"`. */
  adminReinstate(token: string, userId: string): Promise<UserModeration> {
    return this.authed<{ moderation: UserModeration }>("POST", "/admin/membership/reinstate", token, {
      userId,
    }).then((d) => d.moderation);
  }
  /** Ban a user — a full app lockout (docs/105 v1). Never cancels a store subscription;
   * see the module's IAP caveat — this only revokes app-side access. */
  adminBan(token: string, userId: string, reason?: string): Promise<UserModeration> {
    return this.authed<{ moderation: UserModeration }>("POST", "/admin/users/ban", token, {
      userId,
      reason,
    }).then((d) => d.moderation);
  }
  /** Clear a ban back to `"active"`. */
  adminUnban(token: string, userId: string): Promise<UserModeration> {
    return this.authed<{ moderation: UserModeration }>("POST", "/admin/users/unban", token, { userId }).then(
      (d) => d.moderation,
    );
  }
  /** A user's current moderation row, or `null` if they've never been moderated. */
  adminModeration(token: string, userId: string): Promise<UserModeration | null> {
    const q = `/admin/moderation?userId=${encodeURIComponent(userId)}`;
    return this.authed<{ moderation: UserModeration | null }>("GET", q, token).then((d) => d.moderation);
  }

  /** Authenticated admin request (Authorization: Bearer <worker session>). */
  private async authed<T>(method: string, path: string, token: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}`, ...this.deviceHeaders() };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const res = await this.fetchFn(`${this.baseUrl}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    return data as T;
  }

  private async binaryAuthed<T>(method: string, path: string, token: string, body: Blob): Promise<T> {
    const res = await this.fetchFn(`${this.baseUrl}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": body.type || "application/octet-stream", ...this.deviceHeaders() },
      binaryBody: body,
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    return data as T;
  }

  private async post<T>(path: string, body: unknown, sessionId?: string): Promise<T> {
    const headers: Record<string, string> = { "Content-Type": "application/json", ...this.deviceHeaders() };
    if (sessionId) headers["X-Session"] = sessionId;
    const res = await this.fetchFn(`${this.baseUrl}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new BackendError(res.status, data?.error ?? `HTTP ${res.status}`);
    return data as T;
  }
}
